import React, {
    ReactElement, useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Dashboard as ApiDashboard,
    TicketState as ApiTicketState,
    DashboardInput,
    DashboardSettings,
    GetUserDashboardsDocument,
    GetUserDashboardsQuery,
    Permission,
    SaveUserPortalSettingsDocument,
    TicketState,
    TicketView,
    UpdateDashboardDocument,
    UserPortalSettings,
    useListAgentChatQuery,
} from "../../__generated__/api";
import client, { cloneWithoutTypename } from "../../api";
import useLogging, { LoggingHandler } from "../logging/LoggingContext";
import ColumnStateCache from "./column-state-cache";
import DashboardViewProcessor from "./dashboard-view-processor";
import SelectionState from "./selection";
import SocketUpdater from "./socket-updater";
import TicketColumnGroupCache from "./ticket-column-group-cache";
import TicketStateCache from "./ticket-state";
import UserCache from "./user-cache";
import SearchState from "./search-state";
import useCurrentSession from "../session";
import TicketColumnState from "./ticket-column-state";

type Dashboard = ApiDashboard;

type TicketsQuery = ReturnType<typeof useListAgentChatQuery>;

interface ViewState {
    dashboard: Dashboard,
    userSettings: UserPortalSettings
}

type UpdateHandler = () => void;

export class TicketStateContext {
    public static useTicketStateContext(
        chatsQuery: TicketsQuery,
        ticketState: TicketStateCache,
        groupCache: TicketColumnGroupCache,
        selectionState: SelectionState,
        columnState: TicketColumnState,
    ) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [dashboardQuery, setDashboards] = useState<GetUserDashboardsQuery>();

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            LoggingHandler.instance.nextTick(async () => {
                const data = await LoggingHandler.instance.withRetries(() => client.query<GetUserDashboardsQuery>({
                    query: GetUserDashboardsDocument,
                }));
                if (data) {
                    setDashboards({
                        ticketStates: data.ticketStates,
                        userPortalSettings: {
                            ...data.userPortalSettings,
                            dashboards: data.userPortalSettings?.dashboards?.map(dashboard => ({
                                ...dashboard,
                            })) ?? [],
                        },
                    });
                }
            });
        }, []);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [, updater] = useState<unknown>();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const viewStateRef = useRef<Partial<ViewState>>({});

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const columnCache = ColumnStateCache.useColumnStateCache(dashboardQuery?.ticketStates as TicketState[]);
        ticketState.setColumnStateCache(columnCache);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const logging = useLogging();

        const updateHander = () => {
            updater({});
        };

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const searchState = SearchState.useSearchState();

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const instance = useMemo(
            () => {
                const state = new TicketStateContext(
                    dashboardQuery,
                    chatsQuery,
                    updateHander,
                    viewStateRef,
                    ticketState,
                    groupCache,
                    selectionState,
                    columnCache,
                    logging,
                    searchState,
                    columnState,
                );
                searchState.setTicketState(state);
                return state;
            },
            [dashboardQuery, chatsQuery.data, ticketState, columnCache, selectionState.current, searchState, columnState],
        );

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (dashboardQuery) {
                updateHander();
            }
        }, [dashboardQuery]);

        return instance;
    }

    private dashboardViewProcessor?: DashboardViewProcessor;

    // eslint-disable-next-line no-useless-constructor
    constructor(
        private readonly dashboard: GetUserDashboardsQuery | undefined,
        private chatsData: TicketsQuery | undefined,
        private readonly updater: UpdateHandler,
        private readonly _viewStateRef: React.MutableRefObject<Partial<ViewState>>,
        private readonly _tickets: TicketStateCache,
        private readonly groupCache: TicketColumnGroupCache,
        public readonly selectionState: SelectionState,
        public readonly columnCache: ColumnStateCache,
        public readonly logging: LoggingHandler,
        public readonly searchState: SearchState,
        public readonly columnState: TicketColumnState,
    ) {
        // nothing
    }

    get isChatLoading() {
        return this.chatsData?.loading;
    }

    get hasMoreResults() {
        return this.chatsData?.data?.chats?.nextPageToken && this.chatsData?.data?.chats?.nextPageToken != null;
    }

    get userSettings() {
        return this.viewState?.userSettings ?? this.dashboard?.userPortalSettings;
    }

    set userSettings(userSettings: UserPortalSettings) {
        this.setViewState({
            userSettings,
        });
        this.logging.wrap(async () => {
            await client.mutate({
                mutation: SaveUserPortalSettingsDocument,
                variables: {
                    settings: {
                        dashboards: userSettings.dashboards?.map(row => ({
                            dashboardId: row.dashboardId ?? row.dashboard.id,
                            isPinned: row.isPinned ?? false,
                        })),
                        chatSections: userSettings.chatSections?.map(row => ({
                            isMinimized: row.isMinimized ?? false,
                            sectionId: row.sectionId,
                        })),
                    },
                },
            });
        });
    }

    public loadMoreResults() {
        return this.hasMoreResults ? this.chatsData?.fetchMore({
            variables: {
                ...this.chatsData.variables,
                nextPageToken: this.chatsData.data?.chats?.nextPageToken,
            },
            updateQuery(previous, { fetchMoreResult }) {
                const allChats = (previous.chats?.chats ?? [])
                    .concat(fetchMoreResult.chats?.chats ?? [])
                    .dedupe(row => row.id);
                return {
                    chats: {
                        chats: allChats,
                        nextPageToken: fetchMoreResult.chats?.nextPageToken,
                    },
                    __typename: fetchMoreResult.__typename,
                };
            },
        }) : undefined;
    }

    get ticketStates() {
        return this.dashboard?.ticketStates as ApiTicketState[];
    }

    private get viewState() {
        return this._viewStateRef.current as ViewState;
    }

    private setViewState(state: Partial<ViewState>) {
        this._viewStateRef.current = {
            ...this.viewState,
            ...state,
        };
        return this.updater();
    }

    get currentDashboard() {
        let dashboard = this.viewState?.dashboard as Dashboard;
        if (!dashboard) {
            const lastId = this.dashboard?.userPortalSettings?.lastOpenedDashboardId;
            if (lastId) {
                dashboard = (this.dashboard?.userPortalSettings?.dashboards?.find(
                    board => board.dashboard.id === lastId,
                )?.dashboard ?? dashboard) as Dashboard;
            }
        }

        if (!dashboard) {
            dashboard = this.dashboard?.userPortalSettings?.dashboards?.firstElement?.dashboard as Dashboard;
        }

        if (dashboard && this.searchState?.isSearching) {
            dashboard = {
                ...dashboard,
                settings: {
                    ...dashboard.settings,
                    ...this.searchState.dashboardSettings,
                },
            };
        }
        return dashboard;
    }

    set currentDashboard(dashboard: Dashboard) {
        if (this.searchState.isSearching) {
            this.searchState.dashboardSettings = dashboard.settings as DashboardSettings;
            return;
        }

        this.setViewState({
            dashboard,
        });
    }

    get currentDashboardSettings() {
        return this.currentDashboard?.settings ?? {} as DashboardSettings;
    }

    set currentDashboardSettings(value: DashboardSettings) {
        this.saveDashboard({
            ...this.currentDashboard,
            settings: value,
        });
    }

    public saveDashboard(dashboard: DashboardInput, id?: string) {
        const newDashboard = {
            ...dashboard,
            id: id ?? (dashboard as Dashboard).id,
        } as ApiDashboard;
        this.currentDashboard = newDashboard as ApiDashboard;
        if (!this.currentDashboard.id) {
            return;
        }

        if (this.dashboard?.userPortalSettings?.dashboards) {
            this.dashboard.userPortalSettings.dashboards = this.dashboard
                .userPortalSettings
                .dashboards
                .map(row => (row.dashboard.id === this.currentDashboard.id ? {
                    ...row,
                    dashboard: newDashboard,
                } : row));
        }

        this.logging.wrap(async () => {
            await client.mutate({
                mutation: UpdateDashboardDocument,
                refetchQueries: [GetUserDashboardsDocument],
                variables: {
                    id: newDashboard.id,
                    dashboard: {
                        ...cloneWithoutTypename(newDashboard) as DashboardInput,
                        id: undefined,
                    },
                },
            });
        });
    }

    public update() {
        this.setViewState({});
    }

    get tickets() {
        return this._tickets;
    }

    public async refreshLoadedTickets() {
        return this._tickets.refresh();
    }

    get dashboardView(): DashboardViewProcessor | undefined {
        if (!this.isChatLoading && this.chatsData?.data && !this.dashboardViewProcessor) {
            this.dashboardViewProcessor ??= DashboardViewProcessor.create(
                this._tickets,
                this.currentDashboard,
                this.groupCache,
                this.columnCache,
                this.selectionState,
                this.searchState.searchContext,
                this.columnState,
            );
        }

        return this.dashboardViewProcessor;
    }
}

export const TicketContext = React.createContext<TicketStateContext | undefined>(undefined);

export function TicketStateProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const session = useCurrentSession();

    const hasPermissions = session.permissions.has(Permission.ChatsRead)
        && session.entitlementIds.has(session.entitlementIds.keys.TICKETING);

    const chatsQuery = useListAgentChatQuery({
        skip: !hasPermissions,
        variables: {
            filter: TicketView.MyQueues,
        },
    });

    const selectionState = SelectionState.useSelectionState();
    const userCache = UserCache.useUserCache(chatsQuery.data);
    const ticketState = TicketStateCache.useTicketState(chatsQuery, selectionState, userCache);
    const groupCache = TicketColumnGroupCache.useGroupCache();
    const columnState = TicketColumnState.useColumnState();

    const instance = TicketStateContext.useTicketStateContext(chatsQuery, ticketState, groupCache, selectionState, columnState);
    SocketUpdater.useSocketUpdater(chatsQuery.data, instance);

    return (
        <TicketContext.Provider value={instance}>
            {children}
        </TicketContext.Provider>
    );
}

export function useTicketState(): TicketStateContext {
    return useContext(TicketContext) as TicketStateContext;
}
